import React, { FC } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import { observer } from 'mobx-react';

import {
    AppSection,
    AppSectionHeader,
    InputCheckbox,
    InputText,
    Form,
    AppFooter,
    Button,
    ButtonPrimary,
    ActionList,
    ButtonOutlinePrimary
} from '../../../../components-v2/shared';

import useStore from '../../../../store/useStore';
import DefaultRoutes from '../../../../routes/DefaultRoutes';
import { Crm } from '../../../../types/Crm';
import { CrmConfigSettings } from '../../../../types/CrmConfigSettings';
import { crmConfigTypes, RulesActionFactory, unsavedChangesContainerSelectorDefault } from '../../../../constants';
import { CrmRuleConditionSubjects, CrmRulePredicatePicklists } from '../helpers/FormHelper';
import { JSONWithRuleset, RuleEditor } from '../../../RuleEditor/RuleEditor';
import { RuleAction } from '../../../../types';

export interface WelcomeHomeFormProps {
    initialValues: any;
    onSubmit: (values: Crm, actions: FormikHelpers<Crm>, crmType: string) => Promise<void>;
    onValidate: (values: Crm, crmType: string) => Promise<void>;
    enableReinitialize?: boolean;
}

type WelcomeHomeConfiguration = Pick<
    CrmConfigSettings,
    'api_key' | 'community_id' | 'lead_source_id' | 'tour_activity_id' | 'enable_prospect_updates'
>;

interface WelcomeHomeCrm extends Crm {
    configuration: WelcomeHomeConfiguration;
}

const normalizeWelcomeHomeCrm = (values: Partial<WelcomeHomeCrm>): WelcomeHomeCrm => {
    const configuration = values.configuration || ({} as WelcomeHomeConfiguration);

    return {
        ...values,
        configuration: {
            ...configuration,
            enable_prospect_updates: configuration.enable_prospect_updates ?? false
        }
    } as WelcomeHomeCrm;
};

const ruleActions: Array<RuleAction> = [
    RulesActionFactory.ReplaceStringAction('lead_source_id', 'Replace Lead Source ID with new value'),
    RulesActionFactory.ReplaceStringAction('api_key', 'Replace API Key with new value'),
];

export const WelcomeHomeForm: FC<WelcomeHomeFormProps> = observer(({ onSubmit, onValidate, ...props }) => {
    const { store } = useStore();
    const { router } = store;
    const { companyId } = router.params;
    const initialValues = normalizeWelcomeHomeCrm(props.initialValues);

    const handleCancel = () => router.goTo(DefaultRoutes.CrmSelectType, { companyId }, store);

    const handleValidate = async (formikProps: FormikProps<WelcomeHomeCrm>) => {
        await onValidate(normalizeWelcomeHomeCrm(formikProps.values), crmConfigTypes.welcome_home);
    };

    const handleSubmit = async (values: WelcomeHomeCrm, actions: FormikHelpers<WelcomeHomeCrm>) => {
        await onSubmit(normalizeWelcomeHomeCrm(values), actions, crmConfigTypes.welcome_home);
    };

    return (
        <Form
            {...props}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            confirmUnsavedChanges
            unsavedChangesConfig={{
                containerQuerySelectorAll: unsavedChangesContainerSelectorDefault
            }}
        >
            {(formikProps: FormikProps<WelcomeHomeCrm>) => {
                return (
                    <>
                        <AppSection className="field-group-content">
                            <div className="field-group-col">
                                <AppSectionHeader title="CRM Configuration: WelcomeHome" />
                                <InputText
                                    name="name"
                                    label="Name"
                                    helperText="Unique name for this CRM Configuration"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 64 }}
                                />
                                <AppSectionHeader
                                    title="Connection Settings"
                                    subtitle="Contact WelcomeHome support for your exact connection settings"
                                />
                                <InputText
                                    name="configuration.api_key"
                                    label="API Key"
                                    helperText="WelcomeHome API Key"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                            </div>
                            <div className="field-group-col">
                                <AppSectionHeader title="Property Options" />
                                <InputText
                                    name="configuration.community_id"
                                    label="WelcomeHome Community ID"
                                    required={true}
                                    helperText="WelcomeHome Community ID"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.lead_source_id"
                                    label="WelcomeHome Lead Source ID"
                                    required={true}
                                    helperText="WelcomeHome Lead Source ID"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.tour_activity_id"
                                    label="WelcomeHome Tour Activity ID"
                                    required={false}
                                    helperText="WelcomeHome Tour Activity ID"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <AppSectionHeader
                                    title="Prospect Update Settings"
                                    subtitle="Enable this to update existing prospects by matching email/phone."
                                />
                                <InputCheckbox
                                    name="configuration.enable_prospect_updates"
                                    label="Enable Prospect Updates"
                                    formikProps={formikProps}
                                />
                            </div>
                        </AppSection>
                        <RuleEditor
                            formikProps={formikProps as FormikProps<JSONWithRuleset>}
                            availableRuleActions={ruleActions}
                            availableRuleConditionSubjects={CrmRuleConditionSubjects}
                            predicatePicklists={CrmRulePredicatePicklists}
                        />
                        <AppFooter sticky={true}>
                            <ActionList position="end">
                                {formikProps.dirty && (
                                    <Button onClick={handleCancel} data-lc-trigger-unsaved-changes={true}>
                                        Cancel
                                    </Button>
                                )}
                                <ButtonOutlinePrimary onClick={() => handleValidate(formikProps)}>
                                    Validate
                                </ButtonOutlinePrimary>
                                <ButtonPrimary type="submit" disabled={!formikProps.dirty || formikProps.isSubmitting}>
                                    {formikProps.isSubmitting ? 'Saving...' : 'Save'}
                                </ButtonPrimary>
                            </ActionList>
                        </AppFooter>
                    </>
                );
            }}
        </Form>
    );
});
